import { useCallback, useEffect, useState } from "react"

/**
 * Estado de navegación en el QUERY STRING de un solo path.
 * `/dashboard?view=requests&status=pending&from=2026-07-01&id=<uuid>`
 *
 * Reemplaza a un router, y es una decisión de arquitectura, no de conveniencia:
 *
 *  1. EL PATH NUNCA CAMBIA, así que Express no necesita ninguna ruta comodín para el
 *     history fallback. Y sin comodines, nada puede tapar POST /slack/events (que
 *     dejaría el bot muerto con /health en verde) ni /health. Además `router.get("*")`
 *     TIRA al bootear en Express 5 con path-to-regexp v8. Ver src/http.ts.
 *  2. EL HASH QUEDA RESERVADO para los links de recovery de Supabase
 *     (#access_token=…&type=recovery). Un HashRouter intentaría matchear eso como ruta
 *     y rompería el reset de contraseña — por eso NUNCA hay que adoptar uno acá.
 *  3. Los filtros y la fila abierta pasan a ser LINKS COMPARTIBLES y a prueba de
 *     refresh, que es exactamente lo que un admin quiere cuando le escribe a alguien
 *     por una solicitud trabada.
 */
export type QueryState = Record<string, string | undefined>

function read(): QueryState {
  const out: QueryState = {}
  new URLSearchParams(window.location.search).forEach((v, k) => {
    out[k] = v
  })
  return out
}

function write(next: QueryState, replace: boolean): void {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(next)) {
    if (v !== undefined && v !== "") params.set(k, v)
  }
  const qs = params.toString()
  // Se preserva el pathname tal cual (puede ser /dashboard o /dashboard/) y el hash,
  // que puede traer el token de recovery de Supabase.
  const url = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`
  if (replace) window.history.replaceState(null, "", url)
  else window.history.pushState(null, "", url)
}

export function useQueryState(): {
  query: QueryState
  setQuery: (patch: QueryState, opts?: { replace?: boolean }) => void
  resetQuery: (next?: QueryState) => void
} {
  const [query, setQueryState] = useState<QueryState>(read)

  // El botón atrás del browser tiene que funcionar: es la forma natural de cerrar el
  // drawer de detalle y de deshacer un filtro.
  useEffect(() => {
    const onPop = () => setQueryState(read())
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [])

  const setQuery = useCallback((patch: QueryState, opts?: { replace?: boolean }) => {
    setQueryState((prev) => {
      const next = { ...prev, ...patch }
      for (const k of Object.keys(next)) {
        if (next[k] === undefined || next[k] === "") delete next[k]
      }
      write(next, opts?.replace ?? false)
      return next
    })
  }, [])

  const resetQuery = useCallback((next: QueryState = {}) => {
    setQueryState(() => {
      write(next, false)
      return { ...next }
    })
  }, [])

  return { query, setQuery, resetQuery }
}

/** Debounce simple, para la búsqueda por texto. */
export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return debounced
}
